import React, { Suspense } from "react";
import { PageFallback } from "@/components/layout/PageFallback";
import { NavLink, Outlet, useMatch, useNavigate } from "react-router-dom";
import SendVertexLogo from "@/components/brand/SendVertexLogo";
import {
  LayoutDashboard, Users, ListChecks, Send, FileCode2, ShieldCheck, Sparkles,
  Workflow, Server, BarChart3, Settings, LogOut, ChevronRight, Mail, Filter,
  ShieldOff, AlertTriangle, Flame, Globe, Radar, Search, Palette, TrendingUp,
  Shield, Database, UserCheck, Clock, Layers,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { NAV, AUTH } from "@/constants/testIds";
import { BackgroundJobsProvider } from "@/lib/backgroundJobs";
import BackgroundJobsIndicator from "@/components/BackgroundJobsIndicator";

const NavItem = ({ to, icon: Icon, label, testId, tint = "slate" }) => {
  const match = useMatch(to === "/app" ? { path: to, end: true } : { path: to, end: false });
  const isActive = !!match;
  return (
    <NavLink
      to={to}
      data-testid={testId}
      end={to === "/app"}
      className={[
        "group flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
        isActive
          ? "bg-slate-900 text-white"
          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
      ].join(" ")}
    >
      <span
        className={`icon-chip w-7 h-7 transition-colors ${
          isActive ? "bg-white/15 text-white border-white/20" : `tint-${tint}`
        }`}
      >
        <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
      </span>
      <span>{label}</span>
    </NavLink>
  );
};

export default function AppShell() {
  const { user, workspace, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || user?.email || "U")
    .split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const impersonatingOriginalToken = typeof window !== "undefined" ? localStorage.getItem("mc_admin_original_token") : null;

  const returnToAdmin = () => {
    const t = localStorage.getItem("mc_admin_original_token");
    const u = localStorage.getItem("mc_admin_original_user");
    const w = localStorage.getItem("mc_admin_original_workspace");
    if (!t) return;
    localStorage.setItem("mc_token", t);
    if (u) localStorage.setItem("mc_user", u); else localStorage.removeItem("mc_user");
    if (w) localStorage.setItem("mc_workspace", w); else localStorage.removeItem("mc_workspace");
    localStorage.removeItem("mc_admin_original_token");
    localStorage.removeItem("mc_admin_original_user");
    localStorage.removeItem("mc_admin_original_workspace");
    window.location.href = "/app/admin/users";
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid={NAV.shell}>
      {impersonatingOriginalToken && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-slate-900 text-xs font-medium py-1.5 px-4 flex items-center justify-center gap-3 shadow-sm" data-testid="impersonation-banner">
          <span>⚡ Impersonating <b>{user?.email}</b> — actions are audit-logged.</span>
          <button onClick={returnToAdmin} data-testid="impersonation-exit" className="underline hover:no-underline">Return to admin</button>
        </div>
      )}
      {/* Sidebar */}
      <aside
        data-testid={NAV.sidebar}
        className="w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col fixed inset-y-0 left-0 z-40"
      >
        <div className="h-16 flex items-center px-5 border-b border-slate-200">
          <SendVertexLogo size={22} wordmarkClassName="text-base leading-none" />
        </div>

        <div className="px-3 py-4 border-b border-slate-200">
          <div className="text-overline mb-2 px-2">Workspace</div>
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-slate-50 border border-slate-200">
            <div className="w-7 h-7 rounded-md bg-slate-900 text-white grid place-items-center text-[11px] font-bold">
              {(workspace?.name || "W").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900 truncate">{workspace?.name || "Workspace"}</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">{workspace?.plan || "free"} plan</div>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5 scrollbar-thin">
          {user?.is_super_admin && (
            <>
              <div className="text-overline px-2 mb-2 mt-1 text-red-600">Super Admin</div>
              <NavItem to="/app/admin/users" testId="nav-admin-users" icon={Shield} label="Users" tint="rose" />
              <NavItem to="/app/admin/udb-analysis" testId="nav-admin-udb-analysis" icon={Database} label="Data Unification" tint="rose" />
              <div className="my-3 border-t border-slate-100" />
            </>
          )}
          <div className="text-overline px-2 mb-2">Send</div>
          <NavItem to="/app" testId={NAV.dashboard} icon={LayoutDashboard} label="Dashboard" tint="indigo" />
          <NavItem to="/app/campaigns" testId={NAV.campaigns} icon={Send} label="Campaigns" tint="cyan" />
          <NavItem to="/app/templates" testId={NAV.templates} icon={FileCode2} label="Templates" tint="violet" />
          <NavItem to="/app/automations" testId={NAV.automation} icon={Workflow} label="Automations" tint="fuchsia" />
          <NavItem to="/app/bounces" testId={NAV.bounces} icon={AlertTriangle} label="Bounces" tint="rose" />
          <NavItem to="/app/suppressions" testId={NAV.suppressions} icon={ShieldOff} label="Suppressions" tint="rose" />

          <div className="text-overline px-2 mb-2 mt-4">Audience</div>
          <NavItem to="/app/contacts" testId={NAV.contacts} icon={Users} label="Contacts" tint="emerald" />
          <NavItem to="/app/lists" testId={NAV.lists} icon={ListChecks} label="Lists" tint="teal" />
          <NavItem to="/app/segments" testId={NAV.segments} icon={Filter} label="Segments" tint="lime" />
          <NavItem to="/app/audiences" testId="nav-smart-audiences" icon={Layers} label="Smart Audiences" tint="sky" />

          <div className="text-overline px-2 mb-2 mt-4">Intelligence</div>
          <NavItem to="/app/email-database" testId="nav-email-database" icon={Database} label="Email database" tint="violet" />
          <NavItem to="/app/ai-tools" testId={NAV.aiTools} icon={Sparkles} label="AI Studio" tint="pink" />
          <NavItem to="/app/analytics" testId={NAV.analytics} icon={BarChart3} label="Analytics" tint="amber" />

          {/* Infrastructure — visible ONLY to admins and super admins. Managers and
              creators can't tweak sending domains / DKIM / IPs / warmup / team. */}
          {(user?.is_super_admin || user?.role === "admin") && (
            <>
              <div className="text-overline px-2 mb-2 mt-4">Infrastructure</div>
              <NavItem to="/app/smtp" testId={NAV.smtp} icon={Server} label="SMTP" tint="slate" />
              <NavItem to="/app/sender-profiles" testId="nav-sender-profiles" icon={Mail} label="Sender profiles" tint="indigo" />
              <NavItem to="/app/domains" testId="nav-domains" icon={Globe} label="Sending domains" tint="teal" />
              <NavItem to="/app/dns-inspector" testId="nav-dns-inspector" icon={Search} label="DNS inspector" tint="sky" />
              <NavItem to="/app/ip-monitor" testId="nav-ip-monitor" icon={Radar} label="IP monitoring" tint="cyan" />
              <NavItem to="/app/postmaster" testId="nav-postmaster" icon={TrendingUp} label="Postmaster" tint="fuchsia" />
              <NavItem to="/app/whitelabel" testId="nav-whitelabel" icon={Palette} label="White-label" tint="fuchsia" />
              <NavItem to="/app/deliverability" testId={NAV.deliverability} icon={Flame} label="Deliverability" tint="orange" />
              <NavItem to="/app/team" testId="nav-team" icon={UserCheck} label="Team members" tint="amber" />
              <NavItem to="/app/activity" testId="nav-activity" icon={Clock} label="Activity log" tint="slate" />
              <NavItem to="/app/settings" testId={NAV.settings} icon={Settings} label="Settings" tint="slate" />
            </>
          )}
        </nav>

        <div className="border-t border-slate-200 p-3 flex items-center gap-2">
          <NavLink to="/app/profile" data-testid="nav-profile" className="w-8 h-8 rounded-md bg-slate-200 text-slate-900 grid place-items-center text-xs font-bold hover:bg-slate-300">{initials}</NavLink>
          <NavLink to="/app/profile" data-testid="nav-profile-name" className="flex-1 min-w-0 hover:opacity-80">
            <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
          </NavLink>
          <button
            data-testid={AUTH.logoutButton}
            onClick={() => { logout(); navigate("/login"); }}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-900"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className={`flex-1 ml-64 min-w-0 ${impersonatingOriginalToken ? "pt-8" : ""}`}>
        <BackgroundJobsProvider>
          <Suspense fallback={<PageFallback />}><Outlet /></Suspense>
          <BackgroundJobsIndicator />
        </BackgroundJobsProvider>
      </main>
    </div>
  );
}
